import type { Company } from '@gat-ship/types'
import { inject, shallowRef } from 'vue'
import {
  appLoadingKey,
  companyCollectionKey,
  type CompanyCollectionService,
  type CompanyCommands
} from '@gat-ship/types/injection-keys'
import { useCompanySignalREvents } from '@gat-ship/signalr'
import { globalUtils } from '@gat-ship/utils'
import { useCompanyApiCalls } from './useCompanyApiCalls'
import * as companyList from '../model/company-collection'
import {
  decideChangeDetails,
  decideDeactivate,
  decideReactivate
} from '../model/company-commands'

/**
 * App-loading id prefixes for the company mutation commands. The active
 * company `code` is appended as a second id so concurrent mutations on
 * different companies stay distinct; the views check the prefix alone.
 */
export const companyLoadingIds = {
  add: 'add-company-btn',
  edit: 'edit-company-btn',
  deactivate: 'deactivate-company-btn',
  reactivate: 'reactivate-company-btn'
}

/**
 * A composable that returns a data source for companies.
 *
 * This is the *imperative shell* over the functional core in `../model`: it
 * owns the reactive cache, the loading registry, the network calls, and the
 * optimistic-write/rollback choreography. Every decision (sort, upsert, "does
 * this company exist", "what is the next entity") is delegated to the pure
 * helpers in `company-collection` / `company-commands`, so the only cache-write
 * idiom here is `_companies.value = <pure result>`.
 *
 * The loading handle is the app-wide loading registry, provided once at the
 * root by `LoadingProvider` and consumed here via its injection key
 * (`appLoadingKey`, keyed in `@gat-ship/types`) rather than threaded in as a
 * parameter — keeping the datasource UI-free.
 */
export function useCompanyCollection(): CompanyCollectionService {
  const {
    fetchCompaniesRequest,
    addCompanyRequest,
    changeCompanyDetailsRequest,
    deactivateCompanyRequest,
    reactivateCompanyRequest
  } = useCompanyApiCalls()

  const _companies = shallowRef<Company[]>([])

  // ── Cache writes ──────────────────────────────────────────────────
  // The shell's only mutation idiom: assign the ref to a fresh array computed
  // by the functional core. No sorting/splicing/finding logic lives here.

  function findCompany(code: string) {
    return companyList.findCompany(_companies.value, code)
  }

  function updateCompany(updatedCompany: Company) {
    // Replace-if-present (no-op when absent), preserving the prior contract.
    if (companyList.findCompany(_companies.value, updatedCompany.code)) {
      _companies.value = companyList.upsertCompany(
        _companies.value,
        updatedCompany
      )
    }
  }

  function removeCompany(code: string) {
    _companies.value = companyList.removeCompany(_companies.value, code)
  }

  function addCompany(code: string, name: string, description?: string) {
    _companies.value = companyList.addCompany(
      _companies.value,
      code,
      name,
      description
    )
  }

  const signalREvents = useCompanySignalREvents({
    onCompanyAdded: ({ message }) => {
      addCompany(message.code, message.name, message.description ?? undefined)
    },
    onCompanyDetailsChanged: ({ message }) => {
      const decision = decideChangeDetails(
        _companies.value,
        message.code,
        message.name,
        message.description ?? undefined
      )
      if (decision.ok) {
        updateCompany(decision.next)
      }
    },
    onCompanyDeactivated: ({ message }) => {
      const decision = decideDeactivate(_companies.value, message.code)
      if (decision.ok) {
        updateCompany(decision.next)
      }
    },
    onCompanyReactivated: ({ message }) => {
      const decision = decideReactivate(_companies.value, message.code)
      if (decision.ok) {
        updateCompany(decision.next)
      }
    }
  })

  /**
   * Fetches the companies. If the companies are already loaded, it will not be fetched again unless forceFetch is true.
   * @param options.forceFetch - Whether to force a fetch of the companies. @default false
   * @param options.subscribeToEvents - Whether to subscribe to company events. @default true
   * @param options.includeInactive - Whether to include inactive companies in the response. @default true
   */
  async function fetchCompanies(options?: {
    includeInactive?: boolean
    forceFetch?: boolean
    subscribeToEvents?: boolean
  }) {
    if (_companies.value.length === 0 || options?.forceFetch) {
      const includeInactive = options?.includeInactive ?? true
      const result = await fetchCompaniesRequest({ includeInactive })
      _companies.value = companyList.sortCompanies(result.companies)
      const subscribeToEvents = options?.subscribeToEvents ?? true
      if (subscribeToEvents) {
        signalREvents.subscribeToCompanyEvents()
      }
      return result
    }
    return undefined
  }

  const { setLoadingId, removeLoadingId } = inject(appLoadingKey)!

  // Each command is decision → effect: it asks the functional core what should
  // happen (`decide*`), then the shell performs the side effects — loading
  // toggle, command-id generation, the API call, and the cache write. The
  // cache is updated optimistically for `add` (and rolled back on failure);
  // the others write after the request resolves — preserving the
  // pre-extraction modal behavior.
  const commands: CompanyCommands = {
    async add(payload) {
      const loadingId = [companyLoadingIds.add, payload.code]
      setLoadingId(loadingId)
      // Show the company immediately; roll back if the request fails.
      addCompany(payload.code, payload.name, payload.description)
      try {
        await addCompanyRequest(payload, globalUtils.functions.uuid())
      } catch (error) {
        removeCompany(payload.code)
        throw error
      } finally {
        removeLoadingId(loadingId)
      }
    },
    async changeDetails(companyCode, payload) {
      const decision = decideChangeDetails(
        _companies.value,
        companyCode,
        payload.name,
        payload.description
      )
      if (!decision.ok) {
        throw new Error(decision.reason)
      }
      const loadingId = [companyLoadingIds.edit, companyCode]
      setLoadingId(loadingId)
      try {
        await changeCompanyDetailsRequest(
          companyCode,
          payload,
          globalUtils.functions.uuid()
        )
        updateCompany(decision.next)
      } finally {
        removeLoadingId(loadingId)
      }
    },
    async deactivate(companyCode) {
      const decision = decideDeactivate(_companies.value, companyCode)
      if (!decision.ok) {
        throw new Error(decision.reason)
      }
      const loadingId = [companyLoadingIds.deactivate, companyCode]
      setLoadingId(loadingId)
      try {
        await deactivateCompanyRequest(
          companyCode,
          globalUtils.functions.uuid()
        )
        updateCompany(decision.next)
      } finally {
        removeLoadingId(loadingId)
      }
    },
    async reactivate(companyCode) {
      const decision = decideReactivate(_companies.value, companyCode)
      if (!decision.ok) {
        throw new Error(decision.reason)
      }
      const loadingId = [companyLoadingIds.reactivate, companyCode]
      setLoadingId(loadingId)
      try {
        await reactivateCompanyRequest(
          companyCode,
          globalUtils.functions.uuid()
        )
        updateCompany(decision.next)
      } finally {
        removeLoadingId(loadingId)
      }
    }
  }

  // Bound to a local before returning so the extra `signalREvents` member
  // (a `@gat-ship/signalr` value-type, intentionally absent from the
  // runtime-free `CompanyCollectionService` contract) is allowed through
  // structural assignability instead of tripping excess-property checks.
  const service = {
    companies: _companies,
    findCompany,
    removeCompany,
    addCompany,
    updateCompany,
    clearData: (options?: { unsubscribeFromEvents?: boolean }) => {
      _companies.value = []
      if (options?.unsubscribeFromEvents) {
        signalREvents.unsubscribeFromCompanyEvents()
      }
    },
    fetchCompanies,
    commands,
    signalREvents
  }
  return service
}

/** Inject the company collection context provided by a parent provider. */
export function useCompanyCollectionContext() {
  return inject(companyCollectionKey, undefined)
}
